/**
 * OAuth deep-link return capability (src/platform/).
 *
 * Contract: every capability has a working web implementation; native branches
 * load Capacitor plugins dynamically and MUST keep the web fallback. On web
 * this is a complete no-op — the browser already stays inside the app.
 *
 * Inside the Capacitor shells the provider dialog opens in the system browser,
 * which has no Maxflow session. The backend therefore redirects native-origin
 * callbacks to `io.maxflow.app://oauth-callback?...` (see
 * `app/services/oauth/redirects.py:callback_return_base`); the OS hands that URL to the
 * running app, and this module routes it to the in-app callback screen where
 * the session already exists — so ProtectedRoute never bounces to /login.
 *
 * The scheme is the Capacitor appId and must stay in sync with
 * capacitor.config.ts, the Android VIEW intent filter, the iOS
 * CFBundleURLTypes entry, and the backend `native_app_scheme` setting.
 */

import { isNativePlatform } from './detection'

export const NATIVE_APP_SCHEME = 'io.maxflow.app'
export const OAUTH_RETURN_HOST = 'oauth-callback'
export const OAUTH_CALLBACK_ROUTE = '/settings/connections/oauth-callback'

/**
 * Map an incoming deep link to the in-app OAuth callback route.
 *
 * Returns null for anything that is not our OAuth return (share-target intents,
 * push deep links, future schemes) so the caller leaves those alone. The query
 * string is forwarded verbatim — the picker flows (Meta Page, Meta Ads BM,
 * Google asset) carry `status=setup` plus a `session` id that the connections
 * tab needs untouched.
 */
export function oauthReturnRoute(url: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol.toLowerCase() !== `${NATIVE_APP_SCHEME}:`) return null
  if (parsed.host.toLowerCase() !== OAUTH_RETURN_HOST) return null
  return `${OAUTH_CALLBACK_ROUTE}${parsed.search}`
}

/**
 * Route OAuth deep links to `navigate` for as long as the returned disposer is
 * not called. No-op (and resolves to a no-op disposer) on web.
 *
 * Covers both delivery paths: `appUrlOpen` when the app is already running
 * (MainActivity is singleTask, so the intent is delivered to the live task),
 * and `getLaunchUrl()` for the cold-start case where the OS started the app
 * from the link and the listener was registered too late to see the event.
 */
export async function initOAuthReturn(navigate: (route: string) => void): Promise<() => void> {
  if (!isNativePlatform()) return () => undefined

  const go = (url: string | undefined) => {
    const route = url ? oauthReturnRoute(url) : null
    if (route) navigate(route)
  }

  try {
    const { App } = await import('@capacitor/app')
    const handle = await App.addListener('appUrlOpen', ({ url }) => go(url))
    go((await App.getLaunchUrl())?.url)
    return () => void handle.remove()
  } catch {
    return () => undefined
  }
}
